import React, { useState, useEffect } from 'react';
import styles from '../AdminPanel.module.css';
import { MdInfoOutline, MdFilterList, MdDelete, MdWarning } from 'react-icons/md';
import { AdminLogEntry } from '../../../types/user';

const ActivityLogs: React.FC = () => {
  const [logs, setLogs] = useState<AdminLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  // Örnek loglar oluşturan fonksiyon
  const generateSampleLogs = (): AdminLogEntry[] => {
    const actionTypes = [
      'admin_panel_access', 
      'upgrade_to_admin', 
      'upgrade_to_premium', 
      'downgrade_from_premium',
      'suspend_user',
      'unsuspend_user',
      'delete_user',
      'create_announcement',
      'update_city_settings',
      'change_system_settings'
    ];
    
    const admins = [
      { id: '1', name: 'Admin Kullanıcı' },
      { id: '2', name: 'Süper Admin' },
      { id: '3', name: 'Sistem Yöneticisi' }
    ];
    
    // Son 30 gün için log oluştur
    const logs: AdminLogEntry[] = [];
    for (let i = 0; i < 50; i++) {
      const actionType = actionTypes[Math.floor(Math.random() * actionTypes.length)];
      const admin = admins[Math.floor(Math.random() * admins.length)];
      const daysAgo = Math.floor(Math.random() * 30);
      const date = new Date();
      date.setDate(date.getDate() - daysAgo);
      date.setHours(Math.floor(Math.random() * 24), Math.floor(Math.random() * 60), Math.floor(Math.random() * 60));
      
      let details = {};
      if (actionType.includes('user')) {
        details = { targetUserId: `user${Math.floor(Math.random() * 1000)}` };
      } else if (actionType === 'create_announcement') {
        details = { announcementId: `ann${Math.floor(Math.random() * 100)}` };
      }
      
      logs.push({
        id: `log${i}`,
        adminId: admin.id,
        adminName: admin.name,
        action: actionType,
        details,
        timestamp: date.toISOString(),
        ipAddress: `192.168.1.${Math.floor(Math.random() * 255)}`
      });
    }
    
    // Tarihe göre sırala (en yeni en üstte)
    return logs.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  };
  
  useEffect(() => {
    // Gerçek uygulamada burada API'den loglar çekilir
    setLoading(true);
    
    // Demo için zaman aşımı ekleyelim
    setTimeout(() => {
      const sampleLogs = generateSampleLogs();
      setLogs(sampleLogs);
      setLoading(false);
    }, 1000);
  }, []);
  
  // İnsan tarafından okunabilir log mesajı oluşturma
  const getReadableAction = (log: AdminLogEntry): string => {
    switch(log.action) {
      case 'admin_panel_access': 
        return 'Yönetici paneline erişim sağladı';
      case 'upgrade_to_admin': 
        return `${log.details.targetUserId} ID'li kullanıcıyı yönetici yaptı`;
      case 'upgrade_to_premium': 
        return `${log.details.targetUserId} ID'li kullanıcıyı premium üyeliğe yükseltti`;
      case 'downgrade_from_premium': 
        return `${log.details.targetUserId} ID'li kullanıcının premium üyeliğini kaldırdı`;
      case 'suspend_user': 
        return `${log.details.targetUserId} ID'li kullanıcıyı askıya aldı`;
      case 'unsuspend_user': 
        return `${log.details.targetUserId} ID'li kullanıcının askısını kaldırdı`;
      case 'delete_user': 
        return `${log.details.targetUserId} ID'li kullanıcıyı sildi`;
      case 'create_announcement': 
        return `${log.details.announcementId} ID'li duyuru oluşturdu`;
      case 'update_city_settings': 
        return 'Şehir ayarlarını güncelledi';
      case 'change_system_settings': 
        return 'Sistem ayarlarını değiştirdi';
      default: 
        return log.action;
    }
  };
  
  // Log tipine göre stil sınıfı döndürme
  const getLogClass = (log: AdminLogEntry): string => {
    if (log.action.includes('delete')) {
      return styles.dangerLog;
    } else if (log.action.includes('suspend')) {
      return styles.warningLog;
    } else if (log.action.includes('upgrade') || log.action.includes('create')) {
      return styles.successLog;
    } else {
      return styles.infoLog;
    }
  };
  
  // Log verilerini filtreleme
  const filteredLogs = logs.filter(log => {
    if (filter === 'all') return true;
    return log.action.includes(filter);
  });
  
  // Tarihi formatla
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div>
      <h2>İşlem Geçmişi</h2>
      
      <div className={styles.logControls}>
        <button 
          className={styles.filterButton}
          onClick={() => setShowFilters(!showFilters)}
        >
          <MdFilterList className={styles.buttonIcon} />
          Filtrele
        </button>
        
        <button className={styles.deleteButton}>
          <MdDelete className={styles.buttonIcon} />
          Eski Logları Temizle
        </button>
      </div>
      
      {showFilters && (
        <div className={styles.filtersContainer}>
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>İşlem Türü:</label>
            <select 
              className={styles.filterSelect}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              <option value="all">Tümü</option>
              <option value="access">Erişim</option>
              <option value="upgrade">Yükseltme</option>
              <option value="downgrade">Düşürme</option>
              <option value="suspend">Askıya Alma</option>
              <option value="delete">Silme</option>
              <option value="create">Oluşturma</option>
              <option value="update">Güncelleme</option>
            </select>
          </div>
        </div>
      )}
      
      <div className={styles.cardSection}>
        {loading ? (
          <div className={styles.loadingState}>Loglar yükleniyor...</div>
        ) : filteredLogs.length > 0 ? (
          <div className={styles.logsList}>
            {filteredLogs.map(log => (
              <div key={log.id} className={`${styles.logItem} ${getLogClass(log)}`}>
                <div className={styles.logTime}>{formatDate(log.timestamp)}</div>
                <div className={styles.logContent}>
                  <div className={styles.logUser}>{log.adminName}</div>
                  <div className={styles.logAction}>{getReadableAction(log)}</div>
                  <div className={styles.logMetadata}>
                    <span>IP: {log.ipAddress}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={styles.emptyState}>
            <MdInfoOutline className={styles.emptyStateIcon} />
            <p>Seçilen filtrelere uygun log bulunamadı.</p>
          </div>
        )}
      </div>
      
      <div className={styles.cardSection}>
        <div className={styles.warningBox}>
          <MdWarning className={styles.warningIcon} />
          <div>
            <h4>Önemli Bilgi</h4>
            <p>İşlem geçmişi, yönetici faaliyetlerinin denetim kaydıdır ve güvenlik açısından önemlidir. 
              Tüm işlemlerin kim tarafından, ne zaman yapıldığını gösterir. Bu loglar, 
              herhangi bir güvenlik ihlali durumunda kanıt olarak kullanılabilir.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityLogs;
